import { computed, ref, type ComputedRef } from 'vue';
import { z } from 'zod';
import { useEventBus } from '@/hooks/useEventBus.ts';
import { useNotify } from '@/hooks/useNotify.ts';
import { useI18n } from '@/hooks/useI18n.ts';
import { useTimedPageAction } from '@/hooks/useTimedPageAction.ts';
import TimedPageActions from '@/constants/TimedPageActions.ts';
import useDynamicModal from '@/store/hooks/useDynamicModal.ts';
import { SidebarType } from '@/components/RightSidebar.types.ts';
import SmsPhoneSelectorModal from '@/components/sms/SmsPhoneSelectorModal.vue';
import ConversationLead from '@/entities/ConversationLead.ts';
import ConversationLeadPhone from '@/entities/ConversationLeadPhone.ts';
import { useNiotextStore } from '@/store/modules/niotext/store.ts';
import { useSmsProviderAvailability } from '@/components/sms/hooks/useSmsProviderAvailability.ts';
import { ContactTargetSchema, type ApiContactTarget } from '@/api/conversations/types/index.ts';

const LeadPhoneSchema = z.object({
    id: z.number(),
    phone: z.string(),
    type: z.string().nullable().optional(),
    preferred: z.boolean().nullable().optional(),
});

const LeadSchema = z.object({
    id: z.number(),
    fullName: z.string(),
    cellPhones: z.array(LeadPhoneSchema),
    isUnsubscribedToSms: z.boolean(),
    isCaslExpired: z.boolean(),
});

export const OpenDrawerPayloadSchema = z.object({
    lead: LeadSchema,
    selectedPhone: LeadPhoneSchema.optional(),
    contactTarget: ContactTargetSchema.optional(),
    operationGeneration: z.number(),
});

export interface OpenDrawerPayload {
    lead: ConversationLead;
    selectedPhone?: ConversationLeadPhone;
    contactTarget?: ApiContactTarget;
    operationGeneration: number;
}

// Module-level singleton state (shared across all useSmsDrawer() calls)
const openedLeadId = ref<number | null>(null);
const targetCommunicationId = ref<number | null>(null);
const isWaitingForLeadConversation = ref<boolean>(false);
let openIntentGeneration = 0;
let drawerOperationGeneration = 0;
const phoneSelectorModalName = 'sms-phone-selector';

type TimedPageAction = ReturnType<typeof useTimedPageAction>;
interface ActiveDrawerTimer {
    operationGeneration: number;
    timer: TimedPageAction;
}

let activeDrawerTimer: ActiveDrawerTimer | null = null;

export interface OpenOptions {
    targetCommunicationId?: number | null;
    selectedPhone?: ConversationLeadPhone;
    shouldOpen?: () => boolean;
    contactTarget?: ApiContactTarget;
}

interface UseSmsDrawerReturn {
    targetCommunicationId: ComputedRef<number | null>;
    isWaitingForLeadConversation: ComputedRef<boolean>;
    open: (lead: ConversationLead, options?: OpenOptions) => boolean | Promise<boolean>;
    close: () => void;
    closeDrawerOperation: (operationGeneration: number) => void;
    cleanup: () => void;
    prepareLeadConversationOpen: () => void;
    clearLeadConversationOpenPreparation: () => void;
    isDrawerOperationCurrent: (operationGeneration: number) => boolean;
    markDrawerLoadSuccess: (operationGeneration: number) => void;
    markDrawerLoadFailure: (operationGeneration: number, error: unknown) => void;
}

/**
 * Composable for orchestrating the SMS conversation drawer flow.
 * Uses singleton pattern for shared state across components.
 */
export function useSmsDrawer(): UseSmsDrawerReturn {
    const { emit } = useEventBus();
    const { closeModalsByName, openModal } = useDynamicModal();
    const { notify } = useNotify();
    const { t } = useI18n();
    const { ensureSmsProviderAvailable } = useSmsProviderAvailability();

    const open = (lead: ConversationLead, options?: OpenOptions): boolean | Promise<boolean> => {
        closeModalsByName(phoneSelectorModalName);
        const intentGeneration = ++openIntentGeneration;

        if (
            openedLeadId.value === lead.id &&
            !options?.selectedPhone &&
            !options?.targetCommunicationId &&
            !options?.contactTarget
        ) {
            emit('show-right-sidebar', { name: SidebarType.SMS_CONVERSATION });

            return true;
        }

        const openWithProviderAvailability = (providerAvailable: boolean): boolean => {
            if (options?.shouldOpen && !options.shouldOpen()) {
                return false;
            }

            if (intentGeneration !== openIntentGeneration) {
                return false;
            }

            if (!providerAvailable) {
                notify.error(t('smsCommunication.notifications.smsProviderUnavailable'));

                return false;
            }

            if (options?.targetCommunicationId) {
                openDrawer(
                    lead,
                    options.selectedPhone,
                    options.targetCommunicationId,
                    options.contactTarget
                );
                return true;
            }

            if (options?.contactTarget) {
                openDrawer(lead, undefined, undefined, options.contactTarget);
                return true;
            }

            const cellPhones = lead.cellPhones;

            if (cellPhones.length === 0) {
                notify.error(
                    t('smsCommunication.notifications.noMobileNumber', { lead: lead.fullName })
                );
                return false;
            }

            if (options?.selectedPhone) {
                openDrawer(lead, options.selectedPhone, options.targetCommunicationId);
                return true;
            }

            if (cellPhones.length === 1) {
                openDrawer(lead, cellPhones[0], options?.targetCommunicationId);
                return true;
            }

            openPhoneSelection(lead, cellPhones, intentGeneration);

            return true;
        };

        const providerAvailability = ensureSmsProviderAvailable(lead.accountId);

        return providerAvailability instanceof Promise
            ? providerAvailability.then(openWithProviderAvailability)
            : openWithProviderAvailability(providerAvailability);
    };

    const openDrawer = (
        lead: ConversationLead,
        selectedPhone?: ConversationLeadPhone,
        targetId?: number | null,
        contactTarget?: ApiContactTarget
    ): void => {
        cancelActiveDrawerTimer();
        const operationGeneration = ++drawerOperationGeneration;
        openedLeadId.value = lead.id;
        targetCommunicationId.value = targetId ?? null;

        activeDrawerTimer = {
            operationGeneration,
            timer: useTimedPageAction({
                action: TimedPageActions.SMS_MODULE_LOAD_DRAWER,
                additionalData: {
                    leadId: lead.id,
                    leadPhoneId: selectedPhone?.id ?? null,
                    targetCommunicationId: targetId ?? null,
                    ...(contactTarget ? { contactPhoneId: contactTarget.contact_phone_id } : {}),
                },
            }),
        };

        const payload: OpenDrawerPayload = {
            lead,
            ...(selectedPhone ? { selectedPhone } : {}),
            ...(contactTarget ? { contactTarget } : {}),
            operationGeneration,
        };

        emit('show-right-sidebar', {
            name: SidebarType.SMS_CONVERSATION,
            data: payload,
        });
    };

    const cleanup = (): void => {
        openIntentGeneration++;
        drawerOperationGeneration++;
        closeModalsByName(phoneSelectorModalName);
        resetState();
    };

    const close = (): void => {
        useNiotextStore().resetConversationsFetch();
        cleanup();
        emit('hide-right-sidebar', {
            name: SidebarType.SMS_CONVERSATION,
        });
    };

    const closeDrawerOperation = (operationGeneration: number): void => {
        if (operationGeneration !== drawerOperationGeneration) {
            return;
        }

        drawerOperationGeneration++;
        useNiotextStore().resetConversationsFetch();
        resetState();
        emit('hide-right-sidebar', {
            name: SidebarType.SMS_CONVERSATION,
        });
    };

    const prepareLeadConversationOpen = (): void => {
        openIntentGeneration++;
        drawerOperationGeneration++;
        closeModalsByName(phoneSelectorModalName);
        cancelActiveDrawerTimer();
        isWaitingForLeadConversation.value = true;
        useNiotextStore().resetConversationsFetch();
    };

    const clearLeadConversationOpenPreparation = (): void => {
        isWaitingForLeadConversation.value = false;
    };

    const openPhoneSelection = (
        lead: ConversationLead,
        cellPhones: ConversationLeadPhone[],
        intentGeneration: number
    ): void => {
        openModal({
            name: phoneSelectorModalName,
            component: SmsPhoneSelectorModal,
            props: {
                leadName: lead.fullName,
                phoneOptions: cellPhones,
                onSelect: (phone: ConversationLeadPhone) => {
                    if (intentGeneration !== openIntentGeneration) {
                        return;
                    }

                    openIntentGeneration++;
                    onPhoneSelected(phone, lead);
                },
            },
            emits: {
                close: () => {
                    if (intentGeneration !== openIntentGeneration) {
                        return;
                    }

                    openIntentGeneration++;
                },
            },
        });
    };

    const onPhoneSelected = (phone: ConversationLeadPhone, lead: ConversationLead): void => {
        openDrawer(lead, phone);
    };

    const isDrawerOperationCurrent = (operationGeneration: number): boolean =>
        operationGeneration === drawerOperationGeneration;

    const markDrawerLoadSuccess = (operationGeneration: number): void => {
        if (activeDrawerTimer?.operationGeneration !== operationGeneration) {
            return;
        }

        activeDrawerTimer.timer.markSuccess();
        activeDrawerTimer = null;
    };

    const markDrawerLoadFailure = (operationGeneration: number, error: unknown): void => {
        if (activeDrawerTimer?.operationGeneration !== operationGeneration) {
            return;
        }

        try {
            activeDrawerTimer.timer.markFailure(error);
        } catch {
            // markFailure rethrows — swallow here since this is a telemetry-only concern
        }

        activeDrawerTimer = null;
    };

    const cancelActiveDrawerTimer = (): void => {
        if (!activeDrawerTimer) {
            return;
        }

        try {
            activeDrawerTimer.timer.markFailure(new Error('sms_module_load_cancelled'));
        } catch {
            // markFailure rethrows — swallow here since this is a telemetry-only concern
        }

        activeDrawerTimer = null;
    };

    const resetState = (): void => {
        cancelActiveDrawerTimer();

        openedLeadId.value = null;
        targetCommunicationId.value = null;
        clearLeadConversationOpenPreparation();
    };

    return {
        targetCommunicationId: computed(() => targetCommunicationId.value),
        isWaitingForLeadConversation: computed(() => isWaitingForLeadConversation.value),
        open,
        close,
        closeDrawerOperation,
        cleanup,
        prepareLeadConversationOpen,
        clearLeadConversationOpenPreparation,
        isDrawerOperationCurrent,
        markDrawerLoadSuccess,
        markDrawerLoadFailure,
    };
}
